// The inspector: settings of the selected block. Block fields first, then the settings every block
// shares (alignment, spacing, colors, borders, visibility, advanced), all written as Bootstrap classes.
import { h, icon, classList, replaceClasses, findClass } from '../core/dom.js';
import { uiIcons } from './icons.js';
import { blockOf } from '../core/registry.js';
import { renderField } from './fields.js';
import { BREAKPOINTS, isColumnClass } from '../core/grid.js';
import { safeUrl } from '../core/sanitize.js';
import {
    SPACING_SIZES, readSpacing, writeSpacing, isSpacingClass, readHidden, writeHidden, isDisplayClass,
    THEME_COLORS, BACKGROUND_CLASS, TEXT_COLOR_CLASS, TEXT_ALIGN_CLASS, ROUNDED_CLASS, ROUNDED_OPTIONS, SHADOW_CLASS, BORDER_CLASS,
    readStyle, writeStyle, readBackgroundImage, writeBackgroundImage, toHex,
} from '../core/styles.js';

const PROTECTED_CLASS = name => name.startsWith('pflow-') || isColumnClass(name) || name === 'row' || /^container(?:-|$)/.test(name);

export class Inspector {
    constructor(editor, panel) {
        this.editor = editor;
        this.panel = panel;
        this.open = { content: true, layout: true, style: false, visibility: false, advanced: false };
    }

    /** The element the generic settings apply to (the <hr> of a separator, not its editor wrapper). */
    styleTarget(el) {
        return el.hasAttribute('data-pf-wrapper') && el.firstElementChild ? el.firstElementChild : el;
    }

    render() {
        const e = this.editor;
        const t = e.t;
        const el = e.selected;
        this.panel.replaceChildren();
        if (!el || !el.isConnected) {
            this.panel.append(this.emptyState());
            return;
        }
        const def = blockOf(el);
        if (!def) return;
        this.panel.append(this.header(el, def));

        const contentFields = (typeof def.fields === 'function' ? def.fields(el, e.ctx) : def.fields) || [];
        const layoutTypes = new Set(['columns', 'row-actions']);
        const content = contentFields.filter(field => !layoutTypes.has(field.type) && def.kind === 'leaf');
        const layout = contentFields.filter(field => layoutTypes.has(field.type) || def.kind !== 'leaf');
        if (content.length) this.panel.append(this.section('content', t('panel.content'), this.fields(content, el)));
        if (layout.length) this.panel.append(this.section('layout', t('panel.layout'), this.fields(layout, el)));
        this.panel.append(this.section('style', t('panel.style'), this.styleFields(el, def)));
        this.panel.append(this.section('visibility', t('panel.visibility'), this.visibilityFields(el)));
        this.panel.append(this.section('advanced', t('panel.advanced'), this.advancedFields(el, def, contentFields)));
    }

    emptyState() {
        const t = this.editor.t;
        return h('div', { class: 'pf-inspector-empty' },
            icon(uiIcons['cursor-click'], 'pf-empty-icon'),
            h('p', {}, t('inspector.empty')),
            h('ul', { class: 'pf-shortcuts' },
                [['Ctrl+Z', 'shortcut.undo'], ['Ctrl+D', 'shortcut.duplicate'], ['Alt+↑ ↓', 'shortcut.move'], ['Del', 'shortcut.remove'], ['Esc', 'shortcut.parent'], ['Enter', 'shortcut.edit']]
                    .map(([keys, label]) => h('li', {}, h('kbd', {}, keys), ' ', t(label)))));
    }

    header(el, def) {
        const e = this.editor;
        const t = e.t;
        const crumbs = h('div', { class: 'pf-crumbs', role: 'navigation', 'aria-label': t('inspector.path') });
        const chain = [];
        let node = el;
        while (node) {
            chain.unshift(node);
            node = e.parentBlock(node);
        }
        chain.forEach((item, index) => {
            const itemDef = blockOf(item);
            const last = index === chain.length - 1;
            const crumb = h('button', { type: 'button', class: 'pf-crumb' + (last ? ' pf-on' : ''), 'aria-current': last ? 'true' : null }, t(itemDef.label));
            crumb.addEventListener('click', () => e.select(item));
            crumb.addEventListener('mouseenter', () => e.overlay.setHover(item));
            crumbs.append(crumb);
            if (!last) crumbs.append(h('span', { class: 'pf-crumb-sep', 'aria-hidden': 'true' }, '›'));
        });
        const actions = h('div', { class: 'pf-head-actions' },
            e.options.savedBlocks !== false ? this.iconButton('bookmark-simple', t('action.saveBlock'), () => e.app.askSaveBlock()) : null,
            this.iconButton('copy', t('action.duplicate'), () => e.duplicateSelected()),
            this.iconButton('trash', t('action.remove'), () => e.removeSelected(), 'pf-danger'),
            this.iconButton('x', t('action.close'), () => e.select(null)));
        return h('div', { class: 'pf-inspector-head' },
            h('div', { class: 'pf-inspector-title' }, icon(uiIcons[def.icon] || ''), h('strong', {}, t(def.label)), actions),
            chain.length > 1 ? crumbs : null);
    }

    iconButton(name, title, onClick, extra = '') {
        const btn = h('button', { type: 'button', class: `pf-icon-btn ${extra}`, title, 'aria-label': title }, icon(uiIcons[name]));
        btn.addEventListener('click', onClick);
        return btn;
    }

    section(id, title, body) {
        const children = Array.isArray(body) ? body.filter(Boolean) : [body];
        if (!children.length) return null;
        const details = h('details', { class: 'pf-section', open: this.open[id] ? '' : null });
        details.addEventListener('toggle', () => {
            this.open[id] = details.open;
        });
        details.append(h('summary', { class: 'pf-section-title' }, title, icon(uiIcons['caret-down'], 'pf-caret')), h('div', { class: 'pf-section-body' }, ...children));
        return details;
    }

    /** api given to a field: its target and how to apply a change. */
    api(el, target) {
        const e = this.editor;
        return {
            t: e.t,
            editor: e,
            target,
            apply: (fn, { commit = true, rerender = false } = {}) => {
                const result = fn(target);
                // A field may replace its element (another heading level): keep the selection on it.
                if (result && result.nodeType === 1 && result !== target && e.selected === el && target === el) e.selected = result;
                e.overlay.schedule();
                if (commit) e.commit();
                else e.changed();
                if (rerender) this.render();
            },
        };
    }

    fields(list, el) {
        const nodes = [];
        for (const field of list) {
            const target = field.target ? field.target(el) : el;
            if (!target) continue;
            if (typeof field.when === 'function' && !field.when(el)) continue;
            const node = renderField(field, this.api(el, field.type === 'buttons' ? el : target));
            if (node) nodes.push(node);
        }
        return nodes;
    }

    /** Opens the media dialog for an image or a card (double click in the canvas). */
    async pickMedia(block) {
        const e = this.editor;
        const img = block.querySelector('img');
        if (!img) return;
        e.select(block);
        const result = await e.media.pick('image', { onStart: () => block.setAttribute('data-pf-uploading', '') });
        block.removeAttribute('data-pf-uploading');
        if (!result) return;
        img.setAttribute('src', safeUrl(result.url, { image: true }));
        if (!img.getAttribute('alt') && (result.alt || result.label)) img.setAttribute('alt', result.alt || result.label);
        e.commit();
        this.render();
    }

    // ---- Shared settings -------------------------------------------------------------------------------

    classChoice(label, target, pattern, options) {
        return renderField({
            type: options.length <= 5 && options.every(option => option.icon) ? 'segmented' : 'select',
            icons: options.every(option => option.icon),
            label,
            options,
            get: el => findClass(el, pattern),
            set: (el, value) => replaceClasses(el, pattern, value),
        }, this.api(this.editor.selected, target));
    }

    swatches(label, target, pattern, prefix, property) {
        const e = this.editor;
        const t = e.t;
        const current = findClass(target, pattern);
        const custom = readStyle(target, property);
        const list = h('div', { class: 'pf-swatches', role: 'radiogroup', 'aria-label': t(label) });
        const colors = prefix === 'bg' ? [...THEME_COLORS, 'white', 'transparent'] : [...THEME_COLORS, 'white', 'muted'];
        const none = h('button', { type: 'button', class: 'pf-swatch pf-swatch-none' + (!current && !custom ? ' pf-on' : ''), title: t('option.none'), 'aria-label': t('option.none') });
        none.addEventListener('click', () => this.api(e.selected, target).apply(el => {
            replaceClasses(el, pattern, '');
            writeStyle(el, property, '');
        }, { commit: true, rerender: true }));
        list.append(none);
        colors.forEach(color => {
            const className = `${prefix}-${color}`;
            const swatch = h('button', { type: 'button', class: 'pf-swatch' + (current === className ? ' pf-on' : ''), title: color, 'aria-label': color });
            swatch.style.background = this.themeColor(color);
            swatch.addEventListener('click', () => this.api(e.selected, target).apply(el => {
                replaceClasses(el, pattern, className);
                writeStyle(el, property, '');
            }, { commit: true, rerender: true }));
            list.append(swatch);
        });
        const picker = h('input', { type: 'color', class: 'pf-swatch pf-swatch-custom' + (custom ? ' pf-on' : ''), value: toHex(custom) || '#6d5efc', title: t('option.custom'), 'aria-label': t('option.custom') });
        picker.addEventListener('input', () => this.api(e.selected, target).apply(el => {
            replaceClasses(el, pattern, '');
            writeStyle(el, property, picker.value);
        }, { commit: false }));
        picker.addEventListener('change', () => {
            e.commit();
            this.render();
        });
        list.append(picker);
        return h('div', { class: 'pf-field' }, h('span', { class: 'pf-label' }, t(label)), list);
    }

    /** Real colors of the site's theme, read from the canvas. */
    themeColor(name) {
        if (name === 'transparent') return 'repeating-conic-gradient(#ccc 0 25%, #fff 0 50%) 0 0 / 8px 8px';
        this.colorCache = this.colorCache || {};
        if (this.colorCache[name]) return this.colorCache[name];
        const doc = this.editor.canvas.doc;
        const probe = doc.createElement('div');
        probe.className = name === 'muted' ? 'text-muted' : `bg-${name}`;
        probe.style.cssText = 'position:absolute;width:1px;height:1px;visibility:hidden';
        doc.body.append(probe);
        const style = doc.defaultView.getComputedStyle(probe);
        const value = name === 'muted' ? style.color : style.backgroundColor;
        probe.remove();
        this.colorCache[name] = value && value !== 'rgba(0, 0, 0, 0)' ? value : '#ccc';
        return this.colorCache[name];
    }

    spacingBox(target) {
        const t = this.editor.t;
        const select = (property, side) => {
            const values = readSpacing(target, property);
            const options = property === 'm' ? [...SPACING_SIZES, 'auto'] : SPACING_SIZES;
            const control = h('select', { class: 'pf-space-select', 'aria-label': `${t(property === 'm' ? 'field.margin' : 'field.padding')} ${t(`side.${side}`)}` },
                h('option', { value: '' }, '–'), options.map(value => h('option', { value, selected: values[side] === value }, value)));
            control.addEventListener('change', () => this.api(this.editor.selected, target).apply(el => {
                const next = readSpacing(el, property);
                next[side] = control.value;
                writeSpacing(el, property, next);
            }, { commit: true }));
            return control;
        };
        const box = h('div', { class: 'pf-spacing' },
            h('div', { class: 'pf-spacing-margin' },
                h('span', { class: 'pf-spacing-label' }, t('field.margin')),
                h('div', { class: 'pf-sp-t' }, select('m', 't')),
                h('div', { class: 'pf-sp-s' }, select('m', 's')),
                h('div', { class: 'pf-sp-e' }, select('m', 'e')),
                h('div', { class: 'pf-sp-b' }, select('m', 'b')),
                h('div', { class: 'pf-spacing-padding' },
                    h('span', { class: 'pf-spacing-label' }, t('field.padding')),
                    h('div', { class: 'pf-sp-t' }, select('p', 't')),
                    h('div', { class: 'pf-sp-s' }, select('p', 's')),
                    h('div', { class: 'pf-sp-e' }, select('p', 'e')),
                    h('div', { class: 'pf-sp-b' }, select('p', 'b')),
                    h('div', { class: 'pf-spacing-core', 'aria-hidden': 'true' }))));
        return h('div', { class: 'pf-field pf-field-wide' }, h('span', { class: 'pf-label' }, t('field.spacing')), box);
    }

    styleFields(el, def) {
        const t = this.editor.t;
        const target = this.styleTarget(el);
        const nodes = [];
        if (def.name !== 'spacer' && def.name !== 'separator') {
            nodes.push(this.classChoice('field.textAlign', target, TEXT_ALIGN_CLASS, [
                { value: '', label: t('option.default'), icon: 'x' },
                { value: 'text-start', label: t('option.start'), icon: 'text-align-left' },
                { value: 'text-center', label: t('option.center'), icon: 'text-align-center' },
                { value: 'text-end', label: t('option.end'), icon: 'text-align-right' },
            ]));
        }
        nodes.push(this.spacingBox(target));
        if (def.name !== 'separator') nodes.push(this.swatches('field.background', target, BACKGROUND_CLASS, 'bg', 'background-color'));
        if (def.kind !== 'leaf' || def.name === 'text' || def.name === 'card') {
            nodes.push(renderField({
                type: 'media',
                label: 'field.backgroundImage',
                mediaType: 'image',
                get: node => readBackgroundImage(node),
                set: (node, value) => writeBackgroundImage(node, safeUrl(value, { image: true })),
            }, this.api(el, target)));
        }
        if (def.name !== 'spacer') nodes.push(this.swatches('field.textColor', target, TEXT_COLOR_CLASS, 'text', 'color'));
        if (def.name !== 'separator' && def.name !== 'spacer') {
            nodes.push(h('div', { class: 'pf-two' },
                this.classChoice('field.corners', target, ROUNDED_CLASS, ROUNDED_OPTIONS),
                this.classChoice('field.shadow', target, SHADOW_CLASS, [
                    { value: '', label: t('option.none') }, { value: 'shadow-sm', label: t('option.small') }, { value: 'shadow', label: t('option.medium') }, { value: 'shadow-lg', label: t('option.large') },
                ])));
            nodes.push(renderField({
                type: 'toggle',
                label: 'field.border',
                get: node => classList(node).includes('border'),
                set: (node, value) => replaceClasses(node, /^border$/, value ? 'border' : ''),
            }, this.api(el, target)));
        }
        return nodes;
    }

    visibilityFields(el) {
        const t = this.editor.t;
        const target = this.styleTarget(el);
        const hidden = readHidden(target);
        const list = h('div', { class: 'pf-visibility' });
        BREAKPOINTS.forEach(bp => {
            const input = h('input', { type: 'checkbox', checked: !hidden[bp.key] });
            input.addEventListener('change', () => this.api(el, target).apply(node => {
                const next = readHidden(node);
                next[bp.key] = !input.checked;
                writeHidden(node, next);
            }, { commit: true }));
            list.append(h('label', { class: 'pf-visibility-item', title: bp.min ? `≥ ${bp.min}px` : '< 576px' }, input, h('span', {}, bp.label)));
        });
        return [h('div', { class: 'pf-field pf-field-wide' }, h('span', { class: 'pf-label' }, t('field.showOn')), list, h('div', { class: 'pf-help' }, t('help.visibility')))];
    }

    advancedFields(el, def, contentFields) {
        const e = this.editor;
        const t = e.t;
        const target = this.styleTarget(el);
        const managed = [
            ...contentFields.map(field => field.pattern).filter(Boolean),
            TEXT_ALIGN_CLASS, BACKGROUND_CLASS, TEXT_COLOR_CLASS, ROUNDED_CLASS, SHADOW_CLASS, /^border$/,
        ];
        const isManaged = name => PROTECTED_CLASS(name) || isSpacingClass(name) || isDisplayClass(name) || managed.some(pattern => pattern.test(name));
        const free = classList(target).filter(name => !isManaged(name));

        const id = h('input', { type: 'text', class: 'pf-input', value: target.id || '', spellcheck: 'false', placeholder: 'section-name' });
        id.addEventListener('change', () => {
            const value = id.value.trim().replace(/\s+/g, '-');
            const valid = value === '' || /^[A-Za-z][\w-]*$/.test(value);
            id.classList.toggle('pf-invalid', !valid);
            if (valid) this.api(el, target).apply(node => (value ? node.setAttribute('id', value) : node.removeAttribute('id')), { commit: true });
        });

        const classes = h('input', { type: 'text', class: 'pf-input', value: free.join(' '), spellcheck: 'false', placeholder: 'my-class another-class' });
        classes.addEventListener('change', () => this.api(el, target).apply(node => {
            const typed = classes.value.split(/\s+/).filter(name => /^-?[_a-zA-Z][\w-]*$/.test(name) && !PROTECTED_CLASS(name));
            const kept = classList(node).filter(isManaged);
            const all = [...new Set([...kept, ...typed])];
            if (all.length) node.setAttribute('class', all.join(' '));
            else node.removeAttribute('class');
        }, { commit: true, rerender: true }));

        const style = h('textarea', { class: 'pf-input pf-code', rows: 3, spellcheck: 'false', placeholder: 'min-height: 300px;' }, target.getAttribute('style') || '');
        style.addEventListener('change', () => this.api(el, target).apply(node => {
            const value = style.value.trim();
            if (!value) node.removeAttribute('style');
            else if (!/expression\s*\(|javascript:|-moz-binding|behavior\s*:/i.test(value)) node.setAttribute('style', value);
        }, { commit: true, rerender: true }));

        return [
            h('div', { class: 'pf-field' }, h('label', { class: 'pf-label' }, t('field.id')), id, h('div', { class: 'pf-help' }, t('help.id'))),
            h('div', { class: 'pf-field' }, h('label', { class: 'pf-label' }, t('field.classes')), classes, h('div', { class: 'pf-help' }, t('help.classes'))),
            h('div', { class: 'pf-field' }, h('label', { class: 'pf-label' }, t('field.inlineStyle')), style),
        ];
    }
}
